import { useEffect } from "react";

import { FormattedMessage, injectIntl, intlFormatPropTypes } from "react-intl";
import { useSelector } from "react-redux";

import { Badge } from "@ps-design-system/components/Badge";
import { Button } from "@ps-design-system/components/Button";
import { Text } from "@ps-design-system/components/Text";
import XIcon from "@ps-design-system/icons/X/X24";

import { trackButtonClicked } from "../../analytics/shared-analytics";
import { selectProfileSSOid } from "../../app-main/selectors/profile.selectors";
import {
  trackHostHubCalloutClicked,
  trackHostHubCalloutDismissed,
  trackHostHubCalloutViewed,
} from "../analytics/host-hub-analytics";
import dateBasedPricingCollageAsset from "../assets/date-based-pricing-collage.png";
import dateBasedPricingMobileAsset from "../assets/date-based-pricing-mobile.png";
import { DATE_BASED_PRICING_BANNER_TYPE } from "../constants/host-hub-marketing-banners.consts";
import useSetDatePricing from "../hooks/useSetDatePricing";

import { DateBasedPricingListingDialog } from "./DateBasedPricingListingDialog";

type DateBasedPricingBannerProps = {
  dismiss: () => void;
  intl: intlFormatPropTypes;
};

/** Host Hub banner announcing date based pricing to DIY hosts. */
const DateBasedPricingBanner = ({
  dismiss,
  intl,
}: DateBasedPricingBannerProps) => {
  const ssoid = useSelector(selectProfileSSOid);
  const {
    closeListingDialog,
    dateBasedPricingListings,
    goToListingPricing,
    isListingDialogOpen,
    startSetDatePricing,
  } = useSetDatePricing();

  useEffect(() => {
    trackHostHubCalloutViewed({
      calloutType: DATE_BASED_PRICING_BANNER_TYPE,
      ssoid,
    });
  }, [ssoid]);

  const handleCtaClick = () => {
    trackHostHubCalloutClicked({
      calloutType: DATE_BASED_PRICING_BANNER_TYPE,
      ssoid,
    });
    trackButtonClicked({
      buttonLocation: "host hub",
      buttonName: "set date pricing",
    });
    startSetDatePricing();
  };

  const handleDismiss = () => {
    trackHostHubCalloutDismissed({
      calloutType: DATE_BASED_PRICING_BANNER_TYPE,
      ssoid,
    });
    dismiss();
  };

  return (
    <>
      <div
        className="tw-relative tw-flex tw-flex-col tw-gap-16 tw-overflow-hidden tw-rounded-8 tw-bg-gray-900 tw-p-20 tw-text-brand-white md:tw-min-h-[272px] md:tw-flex-row md:tw-items-center lg:tw-flex-col lg:tw-items-stretch xxl:tw-flex-row xxl:tw-items-center"
        data-testid="date-based-pricing-banner"
      >
        <button
          aria-label={intl.formatMessage({
            id: "host_hub.date_based_pricing_banner.dismiss_label",
            defaultMessage: "Dismiss date pricing banner",
          })}
          className="tw-absolute tw-right-16 tw-top-16 tw-z-10 tw-flex tw-h-32 tw-w-32 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-full tw-border-0 tw-bg-transparent tw-text-brand-white"
          data-testid="date-based-pricing-banner-dismiss-button"
          onClick={handleDismiss}
          type="button"
        >
          <XIcon />
        </button>

        <img
          alt=""
          aria-hidden="true"
          className="tw-mx-auto tw-block tw-w-full tw-max-w-[310px] md:tw-hidden lg:tw-block xxl:tw-hidden"
          src={dateBasedPricingMobileAsset}
        />

        <div className="tw-flex tw-flex-1 tw-flex-col tw-items-center tw-gap-16 tw-text-center md:tw-items-start md:tw-text-left lg:tw-items-center lg:tw-text-center xxl:tw-items-start xxl:tw-text-left">
          <Badge appearance="purple" size="sm">
            <FormattedMessage
              id="hosting.date_rate.new_badge"
              defaultMessage="NEW!"
            />
          </Badge>
          <Text
            element="h3"
            typeface="headline-2-m"
            className="tw-m-0 tw-max-w-[256px] tw-text-brand-white"
          >
            <FormattedMessage
              id="host_hub.date_based_pricing_banner.title"
              defaultMessage="Holiday demand is coming. Is your pricing ready?"
            />
          </Text>
          <Text
            element="p"
            typeface="body-2"
            className="tw-m-0 tw-max-w-[325px] tw-text-brand-white"
          >
            <FormattedMessage
              id="host_hub.date_based_pricing_banner.description"
              defaultMessage="With date-based pricing, you can set custom rates for the busiest days of the season."
            />
          </Text>
          <Button
            appearance="white"
            className="tw-w-full md:tw-w-auto lg:tw-w-full xxl:tw-w-auto"
            onClick={handleCtaClick}
            testId="date-based-pricing-banner-cta-button"
            variant="default"
          >
            <FormattedMessage
              id="host_hub.date_based_pricing_banner.cta"
              defaultMessage="Set date pricing"
            />
          </Button>
        </div>

        <img
          alt=""
          aria-hidden="true"
          className="tw-hidden tw-w-[332px] tw-min-w-0 tw-max-w-[45%] md:tw-block lg:tw-hidden xxl:tw-block"
          src={dateBasedPricingCollageAsset}
        />
      </div>
      <DateBasedPricingListingDialog
        listings={dateBasedPricingListings}
        onClose={closeListingDialog}
        onContinue={goToListingPricing}
        open={isListingDialogOpen}
      />
    </>
  );
};

export default injectIntl(DateBasedPricingBanner);
